import React, { useState, useEffect, useRef } from 'react';
import { Personal } from '../types';
import { User, Mail, Calendar, Building } from 'lucide-react';
import PersonalDetailModal from './PersonalDetailModal';
import { useMsal } from '@azure/msal-react';
import { loadUserPhotos } from '../services/photoService';

interface PersonalCardProps {
  personal: Personal;
  layoutMode: 'grid' | 'list';
  onClick?: () => void;
}

const PersonalCard: React.FC<PersonalCardProps> = ({ personal, layoutMode, onClick }) => {
  const [userPhoto, setUserPhoto] = useState<string>('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const hasLoadedPhoto = useRef(false);

  const cardClasses = layoutMode === 'grid'
    ? "card cursor-pointer p-6 transition-all"
    : "card cursor-pointer p-4 transition-all flex items-center gap-4";

  // Carga de fotos CONTROLADA
  useEffect(() => {
    const loadPhotoOnce = async () => {
      if (!personal.email || hasLoadedPhoto.current || !account) return;

      hasLoadedPhoto.current = true;

      try {
        const response = await instance.acquireTokenSilent({
          scopes: ['https://graph.microsoft.com/User.Read.All'],
          account: account,
        });

        const photos = await loadUserPhotos([personal.email], response.accessToken);

        if (photos[personal.email]) {
          setUserPhoto(photos[personal.email]);
        }
      } catch (error) {
        console.error('Error cargando foto:', error);
      }
    };

    loadPhotoOnce();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personal.id]);

  const getInitials = (nombre: string, apellido?: string) => {
    return `${nombre.charAt(0)}${apellido ? apellido.charAt(0) : ''}`.toUpperCase();
  };

  const getRoleBadgeColor = (rol: string) => {
    const roleLower = rol.toLowerCase();
    if (roleLower.includes('head of') || roleLower.includes('director') || roleLower === 'lider un') {
      return 'badge-primary';
    }
    return 'badge';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No especificada';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short'
    });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else {
      setTimeout(() => {
        setShowDetailModal(true);
      }, 10);
    }
  };

  return (
    <>
      <div className={cardClasses} onClick={handleCardClick}>
        {/* Avatar and Basic Info */}
        <div className={layoutMode === 'grid' ? "flex items-start gap-3 mb-4" : "flex items-center gap-3"}>
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700">
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt={`${personal.nombre} ${personal.apellido || ''}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                  <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-sm">
                    {getInitials(personal.nombre, personal.apellido)}
                  </span>
                </div>
              )}
            </div>
            {/* Status indicator */}
            <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white dark:border-neutral-900 ${
              personal.disponible ? 'bg-success' : 'bg-error'
            }`}></div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-1">
              {personal.nombre} {personal.apellido}
            </h3>
            <span className={`${getRoleBadgeColor(personal.rol)} text-xs inline-block`}>
              {personal.rol}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className={layoutMode === 'grid' ? "space-y-2" : "flex-1 grid grid-cols-2 gap-2"}>
          {/* Email */}
          {personal.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
              <span className="text-neutral-600 dark:text-neutral-400 truncate">{personal.email}</span>
            </div>
          )}

          {/* Area */}
          {personal.area && (
            <div className="flex items-center gap-2 text-sm">
              <Building size={14} className="text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
              <div className="flex items-center gap-1.5 truncate">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: personal.area.color || '#2563EB' }}
                ></div>
                <span className="text-neutral-600 dark:text-neutral-400 truncate">{personal.area.nombre}</span>
              </div>
            </div>
          )}

          {/* Fecha de Ingreso */}
          <div className="flex items-center gap-2 text-sm">
            <Calendar size={14} className="text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
            <div className="truncate">
              <span className="text-neutral-500 dark:text-neutral-500 text-xs">Ingreso: </span>
              <span className="text-neutral-600 dark:text-neutral-400">{formatDate(personal.fechaIngreso)}</span>
            </div>
          </div>

          {/* Rol en Servicio */}
          {personal.rolEnServicio && (
            <div className="flex items-center gap-2 text-sm">
              <User size={14} className="text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
              <span className="text-neutral-600 dark:text-neutral-400 truncate">{personal.rolEnServicio}</span>
            </div>
          )}
        </div>

        {/* Availability Status */}
        {layoutMode === 'grid' && (
          <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${
                personal.disponible ? 'bg-success' : 'bg-error'
              }`}></div>
              <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                {personal.disponible ? 'Disponible' : 'Ocupado'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Personal Detail Modal */}
      <PersonalDetailModal
        personal={personal}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        userPhoto={userPhoto}
      />
    </>
  );
};

export default PersonalCard;
